import { WorkflowService } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SpotToast } from "../SpotToast";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { DeleteDialog } from "./DeleteDialog";
import { EntityDialogTitle } from "./EntityDialogTitle";
import { DeleteUsageHistoryOption } from "./DeleteUsageHistoryOption";
import { useCanDeleteHistory } from "@/services/Storage/useCanDeleteHistory";
import { useCloseTabAndRedirect } from "@/components/Layouts/ToolLayout/currentTool";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { useState } from "react";
import {
  deleteResourceUsageHistory,
  HistoryDeletionError,
} from "@/services/Storage/HistoryRetention";

interface IFlowDeleteDialog {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  flowId: string;
  flowName: string;
  flowIcon?: string;
  isAppPublished?: boolean;
}

export const WorkflowDeleteDialog = ({
  isOpen,
  onOpenChange,
  flowId,
  flowName,
  flowIcon,
  isAppPublished,
}: IFlowDeleteDialog) => {
  const { spotGroup } = useSpotUser();
  const closeTabAndRedirect = useCloseTabAndRedirect();
  const queryClient = useQueryClient();
  const canDeleteHistory = useCanDeleteHistory();
  const [deleteHistory, setDeleteHistory] = useState(false);

  const { data: runsPage, isLoading: isLoadingHistoryCount } = useRemoteData(
    DATA_KEYS.ALL_RUNS,
    {
      path: { group_id: spotGroup.id },
      query: { workflow_id: flowId, page: 1, size: 1 },
    },
    { enabled: isOpen && canDeleteHistory }
  );

  const historyCount = runsPage?.total;

  const handleOpenChange = (open: boolean) => {
    if (!open) setDeleteHistory(false);
    onOpenChange(open);
  };

  const deleteWfMutation = useMutation({
    mutationFn: async ({ withHistory }: { withHistory: boolean }) => {
      // History first: purging it is the write that can fail, and once the flow
      // is deleted the dialog is unreachable, so a failure the other way round
      // reads as "couldn't delete the flow" and leaves nothing to retry.
      if (withHistory) {
        try {
          await deleteResourceUsageHistory(spotGroup.id, {
            resource_id: flowId,
            resource_type: "workflow",
          });
        } catch (error: unknown) {
          throw new HistoryDeletionError(error);
        }
      }
      return WorkflowService.deleteWorkflow({
        path: { group_id: spotGroup.id, workflow_id: flowId },
        body: { force: false },
      });
    },
    onSuccess: (_result, { withHistory }) => {
      SpotToast.success(
        withHistory ? "Flow and usage history deleted" : "Flow deleted"
      );
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS],
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.TAGS],
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKSHOP_RECENT],
      });
      if (withHistory) {
        queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ALL_RUNS] });
      }

      closeTabAndRedirect(flowId);
    },
    onError: (err: any) => {
      // The flow is untouched, so keep the dialog open for a retry.
      if (err instanceof HistoryDeletionError) {
        SpotToast.error(
          `Couldn't delete the usage history, so the flow was not deleted`
        );
        return;
      }
      if (err.response?.status === 409) {
        const parent_flows = err.response?.data?.message?.parent_flows || [];
        if (!parent_flows.length) {
          SpotToast.error("Error deleting the flow");
        } else {
          const usage =
            parent_flows.length > 1
              ? `'${parent_flows[0].name}' and ${parent_flows.length - 1} other flow${parent_flows.length - 1 > 1 ? "s" : ""} are still using this flow and so it cannot be deleted.`
              : `'${parent_flows[0].name}' is still using this flow and so it cannot be deleted.`;
          SpotToast.error(usage);
        }
      } else {
        SpotToast.error(`Error deleting the flow`);
      }
    },
    onSettled: (_data, error) => {
      if (error instanceof HistoryDeletionError) return;
      handleOpenChange(false);
    },
  });

  return (
    <DeleteDialog
      title={
        <EntityDialogTitle
          action="Delete"
          icon={flowIcon || "workflow"}
          name={flowName}
        />
      }
      text={
        isAppPublished
          ? `This flow is currently deployed. Deleting it will also delete the deployment, and users will immediately lose access.`
          : `This action will delete this flow. You can restore it later.`
      }
      extra={
        canDeleteHistory ? (
          <DeleteUsageHistoryOption
            checked={deleteHistory}
            onCheckedChange={setDeleteHistory}
            count={historyCount}
            isLoadingCount={isLoadingHistoryCount}
            entryLabel="runs"
            isDisabled={deleteWfMutation.isPending}
          />
        ) : undefined
      }
      onDelete={() => {
        analytics_track(ANALYTICS_EVENTS.WORKFLOW_DELETE, {
          flow_id: flowId,
          is_app_published: isAppPublished ?? false,
          delete_history: deleteHistory,
        });
        if (deleteHistory) {
          analytics_track(ANALYTICS_EVENTS.USAGE_HISTORY_DELETED, {
            resource_type: "flow",
            resource_id: flowId,
            count: historyCount,
          });
        }
        deleteWfMutation.mutate({ withHistory: deleteHistory });
      }}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isLoading={deleteWfMutation.status === "pending"}
    />
  );
};
